import React, { useEffect, useState } from 'react';
import usePrevious from 'lib/src/hooks/usePrevious';

import { Editor } from '@tinymce/tinymce-react';

import { ChallengeDescriptionSectionRequest } from 'src/types/shared/Challenge';
import { tinyMCEPlugins, tinyMCEToolbar } from '@constants/tinyMCEConfig';

import ActionButton from 'lib/src/components/button/ActionButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import FormField from 'lib/src/components/form/FormField';
import TextInput from '@components/form/TextInput';
import useFieldValidation from '@components/form/hooks/useFieldValidation';

const AccordionInput: React.FC<AccordionInputProps> = ({
    value,
    onChange,
    handleDelete,
    handleMoveSection,
    index,
    shouldShowSortButton,
    canMoveUp,
    canMoveDown,
}) => {
    const [error, showError] = useFieldValidation({
        name: `descriptionSections[${index}]`,
        value,
    });
    // const [initialLoad, setInitialLoad] = useState(true);
    const [contentRemainingLength, setContentRemainingLength] = useState<number[]>(
        value.contents?.map(({ content }) => 15000 - content?.length),
    );
    const [contentValues, setContentValues] = useState<string[]>(
        value.contents.map(({ content }) => content),
    ); // Store content strings in state - this is necessary for the tinyMCE editor to work properly with the description builder
    const prevContentValues = usePrevious(contentValues);
    const prevContent = usePrevious(value.contents);

    useEffect(() => {
        if (JSON.stringify(prevContent) !== JSON.stringify(value.contents)) {
            setContentValues(value.contents.map(({ content }) => content));
        }
    }, [value.contents, prevContent]);

    useEffect(() => {
        if (prevContentValues !== contentValues) {
            const newContentRemaining = contentValues.map((content, i) => 15000 - content.length);
            setContentRemainingLength(newContentRemaining);
            const newValue = {
                ...value,
                contents: value.contents.map((item, i) => ({
                    ...item,
                    content: contentValues[i],
                })),
            };
            onChange(index, newValue);
        }
    }, [prevContentValues, contentValues, value, onChange, index]); // update values when state changes

    useEffect(() => {
        value.contents.forEach((content, i) => {
            const newLength = [
                ...contentRemainingLength.slice(0, i),
                15000 - content.content.length,
                ...contentRemainingLength.slice(i + 1, value.contents.length),
            ];

            if (newLength[i] !== contentRemainingLength[i] && !!content.content.length)
                setContentRemainingLength([
                    ...contentRemainingLength.slice(0, i),
                    15000 - content.content.length,
                    ...contentRemainingLength.slice(i + 1, value.contents.length),
                ]);
        });
    }, [value.contents, contentRemainingLength]); // Set chars remaining on mount if value exists

    return (
        <div className="db-input accordion" key={index || undefined}>
            <label>Accordion Input</label>
            <div className={`input-container ${error ? 'error' : ''}`}>
                <FormField name={`descriptionSections[${index}]`} error={error}>
                    <TextInput
                        name={`accordionInput-title-${index}`}
                        value={value.title}
                        placeholder="Accordion title"
                        onChange={_onChange}
                    />
                    {value.contents.map((content, i) => {
                        const canMoveItemUp = i > 0;
                        const canMoveItemDown = i < value.contents.length - 1;
                        // console.log(content?.content);
                        return (
                            <div className="accordion-item" key={i}>
                                <label htmlFor={`accordionInput-${index}-itemTitle=${i}`}>
                                    Title
                                    <TextInput
                                        name={`accordionInput-${index}-itemTitle-${i}`}
                                        value={content.title}
                                        placeholder={`Item ${i + 1} title`}
                                        onChange={_onChange}
                                    />
                                </label>
                                <label htmlFor={`accordionInput-${index}-itemContent-${i}`}>
                                    Content
                                    <Editor
                                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                                        licenseKey="gpl"
                                        onEditorChange={value => {
                                            const newContentValues = [...contentValues];
                                            newContentValues[i] = value;
                                            setContentValues(newContentValues);
                                            _onChange(
                                                `accordionInput-${index}-itemContent-${i}`,
                                                value,
                                            );
                                        }}
                                        value={contentValues[i]}
                                        onInit={(_, editor) => {
                                            if (content?.content)
                                                editor.setContent(contentValues[i]);
                                        }}
                                        onBlur={showError}
                                        init={{
                                            height: 500,
                                            menubar: false,
                                            plugins: tinyMCEPlugins,
                                            toolbar: tinyMCEToolbar,
                                            paste_as_text: true,
                                        }}
                                    />
                                    <p className="charsremaining">
                                        {contentRemainingLength[i] || '10000'} characters remaining
                                    </p>
                                </label>
                                <div className="width-12">
                                    <ButtonRow alignment="right">
                                        {i > 0 && (
                                            <ActionButton
                                                type="button"
                                                onClick={() => _handleMoveItem(i, -1)}
                                                color="grey"
                                                icon="chevron-up"
                                                disabled={!canMoveItemUp}
                                            >
                                                Move up
                                            </ActionButton>
                                        )}
                                        {i < value.contents.length - 1 && (
                                            <ActionButton
                                                type="button"
                                                onClick={() => _handleMoveItem(i, 1)}
                                                color="grey"
                                                icon="chevron-down"
                                                disabled={!canMoveItemDown}
                                            >
                                                Move down
                                            </ActionButton>
                                        )}
                                        <ActionButton
                                            type="button"
                                            color="red"
                                            onClick={() => _handleDeleteItem(i)}
                                        >
                                            Delete
                                        </ActionButton>
                                    </ButtonRow>
                                </div>
                            </div>
                        );
                    })}
                    <div style={{ marginTop: 16 }}>
                        <ButtonRow alignment="right">
                            <ActionButton type="button" color="blue" onClick={_handleAddItem}>
                                Add Accordion item
                            </ActionButton>
                            {!!handleMoveSection && shouldShowSortButton && (
                                <>
                                    <ActionButton
                                        type="button"
                                        onClick={() => handleMoveSection(index, -1)}
                                        color="grey"
                                        icon="chevron-up"
                                        disabled={!canMoveUp}
                                    >
                                        Move up
                                    </ActionButton>
                                    <ActionButton
                                        type="button"
                                        onClick={() => handleMoveSection(index, 1)}
                                        color="grey"
                                        icon="chevron-down"
                                        disabled={!canMoveDown}
                                    >
                                        Move down
                                    </ActionButton>
                                </>
                            )}
                            {!!handleDelete && (
                                <ActionButton
                                    type="button"
                                    color="red"
                                    onClick={() => handleDelete(index)}
                                >
                                    Delete Section
                                </ActionButton>
                            )}
                        </ButtonRow>
                    </div>
                </FormField>
            </div>
        </div>
    );

    function _onChange(name: string, content: string) {
        if (name === `accordionInput-title-${index}`) onChange(index, { ...value, title: content });
        else if (name.startsWith(`accordionInput-${index}-itemTitle-`)) {
            const i: string | number | undefined = name.split('-').pop();
            if (typeof i === 'string')
                onChange(index, {
                    ...value,
                    contents: [
                        ...value.contents.slice(0, +i),
                        { ...value.contents[+i], title: content },
                        ...value.contents.slice(+i + 1, value.contents.length),
                    ],
                });
        } else if (name.startsWith(`accordionInput-${index}-itemContent-`)) {
            const i: string | number | undefined = name.split('-').pop();
            if (typeof i === 'string') {
                const lengthWithTags: number = value.contents[+i].content.length;
                const charsRemaining = 15000 - lengthWithTags;
                setContentRemainingLength([
                    ...contentRemainingLength.slice(0, +i),
                    charsRemaining,
                    ...contentRemainingLength.slice(+i + 1, value.contents.length),
                ]);
                onChange(index, {
                    ...value,
                    contents: [
                        ...value.contents.slice(0, +i),
                        { ...value.contents[+i], content },
                        ...value.contents.slice(+i + 1, value.contents.length),
                    ],
                });
            }
        }
    }

    function _handleAddItem() {
        onChange(index, {
            ...value,
            contents: [...value.contents, { title: '', content: '', sort: value.contents.length }],
        });
    }

    function _handleDeleteItem(item: number) {
        onChange(index, {
            ...value,
            contents: [
                ...value.contents.slice(0, item),
                ...value.contents.slice(item + 1, value.contents.length),
            ],
        });
    }

    function _handleMoveItem(i: number, offset: number) {
        if (i + offset < 0 || i + offset >= value.contents.length) return;

        const contents = [...value.contents];

        const item1 = contents[i];
        const item2 = contents[i + offset];

        contents[i] = item2;
        contents[i + offset] = item1;

        onChange(index, {
            ...value,
            contents: contents.map((item, index) => ({ ...item, sort: index })),
        });

        const newContentValues = [...contentValues];
        const cItem1 = contentValues[i];
        const cItem2 = contentValues[i + offset];
        newContentValues[i] = cItem2;
        newContentValues[i + offset] = cItem1;

        const newContentRemainingLength = [...contentRemainingLength];
        const cLItem1 = contentRemainingLength[i];
        const cLItem2 = contentRemainingLength[i + offset];
        newContentRemainingLength[i] = cLItem2;
        newContentRemainingLength[i + offset] = cLItem1;

        setContentValues(newContentValues);
        setContentRemainingLength(newContentRemainingLength);
    }
};

interface AccordionInputProps {
    value: ChallengeDescriptionSectionRequest;
    onChange: (index: number, newData: ChallengeDescriptionSectionRequest) => void;
    handleDelete?: (index: number) => void;
    handleMoveSection?: (index: number, moveBy: number) => void;
    index: number;
    shouldShowSortButton: boolean;
    API_URL?: string;
    S3_URL?: string;
    canMoveUp: boolean;
    canMoveDown: boolean;
}

export default AccordionInput;
